import { queryClient, queryKeys } from './queryClient'
import { api } from './variables'
import axios from '../helpers/api_helper'
import { usePlatforms } from '../contexts/PlatformsContext'
import { buildKeywordSearchParams, KEYWORD_SEARCH_PATH, normalizeSearchGames } from './keywordSearch'

// API functions for data fetching
export const apiService = {
  // Dashboard APIs
  // fetchTrendingMeta: async () => {
  //   const response = await axios.get(`${api}/tracker/stats/meta/trending`)
  //   return response.data
  // },

  // fetchNewReleasesMeta: async () => {
  //   const response = await axios.get(`${api}/tracker/stats/meta/releases`)
  //   return response.data
  // },

  // Fetch top tracked apps with optional filter params.
  // Uses the Top Tracked endpoint which accepts various query params (genre, bundle, price, rating, revenue, release_date, number_of_players, sort_by, etc.)
  // Accepts `filters` object where array values will be joined by ", ".
  fetchTrackedApps: async (filters = {}) => {
    const token = localStorage.getItem('authToken')

    // Build params similar to keyword tracking helper
    const params = {}
    if (filters && typeof filters === 'object') {
      Object.entries(filters).forEach(([k, v]) => {
        if (v === undefined || v === null || v === '') return
        if (Array.isArray(v)) params[k] = v.join(',')
        else params[k] = v
      })
    }

    const queryString = new URLSearchParams(params).toString()
    // Use explicit backend URL as requested
    const url = `${api}/tracker/dashboard/top_tracked${queryString ? `?${queryString}` : ''}`

    const headers = {}
    if (token) headers.Authorization = `Bearer ${token}`
    const response = await axios.get(url, { headers })
    return response.data
  },

  // Fetch top performance apps (KeyQuest App Score) with optional filter params.
  // Mirrors fetchTrackedApps but calls the top_perfomance endpoint and accepts the same filter keys.
  fetchTopPerformance: async (filters = {}) => {
    const token = localStorage.getItem('authToken')

    const params = {}
    if (filters && typeof filters === 'object') {
      Object.entries(filters).forEach(([k, v]) => {
        if (v === undefined || v === null || v === '') return
        if (Array.isArray(v)) params[k] = v.join(',')
        else params[k] = v
      })
    }

    const queryString = new URLSearchParams(params).toString()
    const url = `${api}/tracker/dashboard/top_perfomance${queryString ? `?${queryString}` : ''}`

    const headers = {}
    if (token) headers.Authorization = `Bearer ${token}`
    const response = await axios.get(url, { headers })

    return response.data
  },

  // Fetch the current user's own tracked apps (same endpoint the App Tracking page uses).
  // Returns the flat list of `game_details` objects, unlike fetchTrackedApps which hits the
  // separate "top tracked" leaderboard endpoint and isn't scoped to what the user actually tracks.
  fetchMyTrackedApps: async () => {
    const token = localStorage.getItem('authToken')
    const headers = {}
    if (token) headers.Authorization = `Bearer ${token}`
    const response = await axios.get(`${api}/tracker/tracking/app`, { headers })

    const games = response.data?.games || []
    // Exclude apps beyond the user's plan limit — the App Tracking page marks these
    // `is_locked: true` and blurs them; deep-linking into them would just bounce the
    // user into an upgrade modal with no way back, so they shouldn't be offered here.
    return games
      .map(g => g.game_details || g)
      .filter(Boolean)
      .filter(g => !g.is_locked)
  },

  // Fetch app graph data (used by Game Dashboard to render App Score graph)
  // POST JSON body: { item_id: <id> }
  fetchAppGraph: async (item_id) => {
    const token = localStorage.getItem('authToken')
    const url = `${api}/tracker/dashboard/app_graph`
    const headers = { 'Content-Type': 'application/json' }
    if (token) headers.Authorization = `Bearer ${token}`
    const response = await axios.post(url, { item_id }, { headers })

    return response.data
  },

  // Category Analysis APIs
  fetchCategoryKeywords: async () => {
    const response = await axios.get(`${api}/tracker/dashboard/trending_keywords`)
    return response.data
  },

  fetchCategoryData: async (category) => {
    const token = localStorage.getItem('authToken')
    const headers = {}
    if (token) headers.Authorization = `Bearer ${token}`
    const response = await axios.get(`${api}/tracker/category/${category}`, { headers })
    return response.data
  },

  // Publisher Analysis APIs
  fetchPublisherData: async (itemId) => {
    const response = await axios.get(`${api}/tracker/tracking/publisher?item_id=${itemId}`)
    return response.data
  },

  fetchPublisherAppDetails: async (appId) => {
    const response = await axios.get(`${api}/tracker/app/${appId}`)
    return response.data
  },

  // // App Tracking APIs
  // fetchAppTrackingData: async (params = {}) => {
  //   const token = localStorage.getItem('authToken')
  //   const queryString = new URLSearchParams(params).toString()
  //   const headers = {}
  //   if (token) headers.Authorization = `Bearer ${token}`
  //   const response = await axios.get(`${api}/tracker/apps${queryString ? `?${queryString}` : ''}`, { headers })
  //   return response.data
  // },

  // Keyword Tracking APIs
  fetchKeywordTrackingData: async (keyword) => {
    const token = localStorage.getItem('authToken')
    const headers = {}
    if (token) headers.Authorization = `Bearer ${token}`
    const params = new URLSearchParams(buildKeywordSearchParams(keyword))
    const response = await axios.get(`${api}${KEYWORD_SEARCH_PATH}?${params.toString()}`, { headers })
    if (response.data && Array.isArray(response.data.games)) {
      response.data.games = normalizeSearchGames(response.data.games)
    }
    return response.data
  },

  fetchPredefinedKeywords: async () => {
    try {
      // New explicit backend endpoint for trending keywords (returns { message, data: [...], details, key, updated_at })
      const url = `${api}/tracker/dashboard/trending_keywords`
      const response = await axios.get(url)
      // Normalize to previous shape expected by callers: { keywords: [...] }
      const keywords = Array.isArray(response?.data?.data) ? response.data.data : []
      return { keywords }
    } catch (error) {
      // Return the error response data if it contains upgrade information
      if (error.response?.data) {
        // Try to normalize if possible
        const resp = error.response.data
        if (Array.isArray(resp?.data)) return { keywords: resp.data }
        if (Array.isArray(resp?.keywords)) return { keywords: resp.keywords }
        return resp;
      }
      throw error
    }
  },

  // `legacy: true` (used by the Keyword Explorer page only) keeps hitting the
  // old /tracker/keyword/{keyword} endpoint instead of KEYWORD_SEARCH_PATH.
  fetchKeywordSearchResults: async (keyword, page = 1, platform = '', filters = {}, { legacy = false } = {}) => {
    const token = localStorage.getItem('authToken')
    try {
      // Build query params
      // Always include platform in params (may be empty string) so the
      // backend receives `platform=` even when no platform is selected.
      const params = { page, platform: platform === undefined ? '' : platform }
      // Merge explicit filters. Expected filter keys: genre, bundle, number_of_players, price, rating, etc.
      if (filters && typeof filters === 'object') {
        Object.entries(filters).forEach(([k, v]) => {
          if (v === undefined || v === null || v === '') return
          // If value is array, join by comma + space to match API expected format (e.g. "4, 3")
          if (Array.isArray(v)) params[k] = v.join(', ')
          else params[k] = v
        })
      }

      // Debug log: show constructed URL and params for keyword search
      // eslint-disable-next-line no-console
      const headers = {}
      if (token) headers.Authorization = `Bearer ${token}`

      if (legacy) {
        const key = encodeURIComponent(String(keyword || ''))
        const queryString = new URLSearchParams(params).toString()
        const url = `${api}/tracker/keyword/${key}${queryString ? `?${queryString}` : ''}`
        const response = await axios.get(url, { headers })
        return response.data
      }

      const queryString = new URLSearchParams(buildKeywordSearchParams(keyword, params)).toString()
      const url = `${api}${KEYWORD_SEARCH_PATH}${queryString ? `?${queryString}` : ''}`
      const response = await axios.get(url, { headers })
      if (response.data && Array.isArray(response.data.games)) {
        response.data.games = normalizeSearchGames(response.data.games)
      }
      return response.data
    } catch (error) {
      // Return the error response data if it contains upgrade information
      if (error.response?.data) {
        return error.response.data;
      }
      throw error
    }
  },

  // Game Dashboard APIs
  fetchGameDetails: async (gameId) => {
    const response = await axios.get(`${api}/tracker/app/${gameId}`)
    return response.data
  },

  fetchGameAnalytics: async (gameId, params = {}) => {
    const token = localStorage.getItem('authToken')
    const queryString = new URLSearchParams(params).toString()
    const headers = {}
    if (token) headers.Authorization = `Bearer ${token}`
    const response = await axios.get(`${api}/tracker/xr/app/${gameId}${queryString ? `?${queryString}` : ''}`, { headers })
    return response.data
  },

  // User Profile APIs
  fetchUserProfile: async () => {
    const token = localStorage.getItem('authToken')
    const headers = {}
    if (token) headers.Authorization = `Bearer ${token}`
    const response = await axios.get(`${api}/user/get-profile`, { headers })
    return response.data
  },

  fetchUserPlans: async () => {
    const token = localStorage.getItem('authToken')
    const headers = {}
    if (token) headers.Authorization = `Bearer ${token}`
    const response = await axios.get(`${api}/payments/current_plan`, { headers })
    return response.data
  },

  fetchUserPaymentMethods: async () => {
    const token = localStorage.getItem('authToken')
    const headers = {}
    if (token) headers.Authorization = `Bearer ${token}`
    const response = await axios.get(`${api}/user/payment-methods`, { headers })
    return response.data
  },

  // Keyword Tracking Landing Page APIs
  fetchUserTrackedKeywords: async () => {
    const token = localStorage.getItem('authToken')
    const headers = { Accept: "application/json, text/plain, */*" }
    if (token) headers.Authorization = `Bearer ${token}`
    const response = await axios.get(`${api}/tracker/tracking/get_user_tracked_keywords`, { headers })
    return response.data
  },

  fetchKeywordGroups: async () => {
    const token = localStorage.getItem('authToken')
    const headers = { Accept: "application/json" }
    if (token) headers.Authorization = `Bearer ${token}`
    const response = await axios.get(`${api}/tracker/tags.keywords?tag_type=KEYWORD`, { headers })
    return response.data
  },

  removeTrackedKeyword: async (gameId) => {
    const token = localStorage.getItem('authToken')
    const headers = { "Content-Type": "application/json" }
    if (token) headers.Authorization = `Bearer ${token}`
    const response = await axios.post(`${api}/tracker/tracking/remove_entity`, {
      game_id: gameId,
      entity_type: "keyword",
      entity_id: null
    }, { headers })
    return response.data
  },

  // Category Tracking Landing Page APIs
  fetchUserTrackedCategories: async () => {
    const token = localStorage.getItem('authToken')
    const headers = { Accept: "application/json, text/plain, */*" }
    if (token) headers.Authorization = `Bearer ${token}`
    const response = await axios.get(`${api}/tracker/tracking/get_user_tracked_categories`, { headers })
    return response.data
  },

  fetchCategoryGroups: async () => {
    const token = localStorage.getItem('authToken')
    const headers = { Accept: "application/json" }
    if (token) headers.Authorization = `Bearer ${token}`
    const response = await axios.get(`${api}/tracker/tags.categories?tag_type=CATEGORY`, { headers })
    return response.data
  },

  removeTrackedCategory: async (gameId) => {
    const token = localStorage.getItem('authToken')
    const headers = { "Content-Type": "application/json" }
    if (token) headers.Authorization = `Bearer ${token}`
    const response = await axios.post(`${api}/tracker/tracking/remove_entity`, {
      game_id: gameId,
      entity_type: "category",
      entity_id: null
    }, { headers })
    return response.data
  },

  // App APIs
  fetchAppDetails: async (appId) => {
    const response = await axios.get(`${api}/app/${appId}`)
    return response.data
  },

  // Category APIs
  fetchCategoryDetails: async (categoryId) => {
    const response = await axios.get(`${api}/category/${categoryId}`)
    return response.data
  },

  // Publisher APIs
  fetchPublisherDetails: async (publisherId) => {
    const response = await axios.get(`${api}/publisher/${publisherId}`)
    return response.data
  },

  fetchPublisherApps: async (publisherId) => {
    const response = await axios.get(`${api}/publisher/${publisherId}/apps`)
    return response.data
  },

  // Keyword APIs
  fetchKeywordDetails: async (keyword) => {
    const response = await axios.get(`${api}/keyword/${keyword}`)
    return response.data
  },

  // Meta Insight APIs
  // fetchTopApps: async () => {
  //   const response = await axios.get(`${api}/meta-insight/top-apps`)
  //   return response.data
  // },

  // fetchPopular: async () => {
  //   const response = await axios.get(`${api}/meta-insight/popular`)
  //   return response.data
  // },

  // fetchMostPopular: async () => {
  //   const response = await axios.get(`${api}/meta-insight/most-popular`)
  //   return response.data
  // },

  // fetchPreOrders: async () => {
  //   const response = await axios.get(`${api}/meta-insight/pre-orders`)
  //   return response.data
  // },

  // Custom Insight APIs
  fetchTopGrossing: async () => {
    const response = await axios.get(`${api}/custom-insight/top-revenued`)
    return response.data
  },

  fetchTopImproved: async () => {
    const response = await axios.get(`${api}/custom-insight/all-time-hits`)
    return response.data
  },

  fetchHits: async () => {
    const response = await axios.get(`${api}/custom-insight/top-keywords-categories`)
    return response.data
  },

  fetchTopRated: async () => {
    const response = await axios.get(`${api}/custom-insight/top-rated`)
    return response.data
  },

  fetchTopDownloaded: async () => {
    const response = await axios.get(`${api}/custom-insight/top-downloaded`)
    return response.data
  },

  fetchTopReviewed: async () => {
    const response = await axios.get(`${api}/custom-insight/top-reviewed`)
    return response.data
  },
}

// Prefetch manager for intelligent data loading
export class PrefetchManager {
  constructor() {
    this.prefetchQueue = new Set()
    this.isProcessing = false
  }

  // Add item to prefetch queue
  addToPrefetchQueue(prefetchFn) {
    this.prefetchQueue.add(prefetchFn)
    this.processPrefetchQueue()
  }

  // Process prefetch queue
  async processPrefetchQueue() {
    if (this.isProcessing || this.prefetchQueue.size === 0) return

    this.isProcessing = true

    try {
      const promises = Array.from(this.prefetchQueue).map(fn => fn())
      await Promise.allSettled(promises)
    } catch (error) {
      console.warn('Prefetch error:', error)
    } finally {
      this.prefetchQueue.clear()
      this.isProcessing = false
    }
  }

  // Prefetch dashboard data
  prefetchDashboard() {
    const prefetchFn = async () => {
      // Get active platforms dynamically
      // Note: This would need to be called from a component with platforms context
      // For now, prefetch the main trending and new releases endpoints
      const trendingCache = queryClient.getQueryData(queryKeys.dashboard.trending())
      const newReleasesCache = queryClient.getQueryData(queryKeys.dashboard.newReleases())

      const promises = []

      if (!trendingCache) {
        promises.push(
          queryClient.prefetchQuery({
            queryKey: queryKeys.dashboard.trending(),
            queryFn: () => apiService.fetchTrendingByPlatform('all'),
            staleTime: 1000 * 60 * 5, // 5 minutes
          })
        )
      }

      if (!newReleasesCache) {
        promises.push(
          queryClient.prefetchQuery({
            queryKey: queryKeys.dashboard.newReleases(),
            queryFn: () => apiService.fetchNewReleasesByPlatform('all'),
            staleTime: 1000 * 60 * 5,
          })
        )
      }

      return Promise.allSettled(promises)
    }

    this.addToPrefetchQueue(prefetchFn)
  }

  // Prefetch app details
  prefetchAppDetails(appId) {
    if (!appId) return

    const prefetchFn = async () => {
      const cached = queryClient.getQueryData(queryKeys.apps.detail(appId))
      if (cached) return

      return queryClient.prefetchQuery({
        queryKey: queryKeys.apps.detail(appId),
        queryFn: () => apiService.fetchAppDetails(appId),
        staleTime: 1000 * 60 * 5,
      })
    }

    this.addToPrefetchQueue(prefetchFn)
  }

  // Prefetch category data
  prefetchCategoryData(categoryId) {
    if (!categoryId) return

    const prefetchFn = async () => {
      const cached = queryClient.getQueryData(queryKeys.categories.detail(categoryId))
      if (cached) return

      return queryClient.prefetchQuery({
        queryKey: queryKeys.categories.detail(categoryId),
        queryFn: () => apiService.fetchCategoryDetails(categoryId),
        staleTime: 1000 * 60 * 5,
      })
    }

    this.addToPrefetchQueue(prefetchFn)
  }

  // Prefetch publisher data
  prefetchPublisherData(publisherId) {
    if (!publisherId) return

    const prefetchFn = async () => {
      const detailsCached = queryClient.getQueryData(queryKeys.publishers.detail(publisherId))
      const appsCached = queryClient.getQueryData(queryKeys.publishers.apps(publisherId))

      const promises = []

      if (!detailsCached) {
        promises.push(
          queryClient.prefetchQuery({
            queryKey: queryKeys.publishers.detail(publisherId),
            queryFn: () => apiService.fetchPublisherDetails(publisherId),
            staleTime: 1000 * 60 * 5,
          })
        )
      }

      if (!appsCached) {
        promises.push(
          queryClient.prefetchQuery({
            queryKey: queryKeys.publishers.apps(publisherId),
            queryFn: () => apiService.fetchPublisherApps(publisherId),
            staleTime: 1000 * 60 * 5,
          })
        )
      }

      return Promise.allSettled(promises)
    }

    this.addToPrefetchQueue(prefetchFn)
  }

  // Prefetch platform insight data (formerly meta insight)
  prefetchPlatformInsight(type) {
    const prefetchFn = async () => {
      let queryKey, apiFn

      switch (type) {
        case 'topApps':
          queryKey = queryKeys.platformInsight.topApps()
          apiFn = apiService.fetchTopApps
          break
        case 'popular':
          queryKey = queryKeys.platformInsight.popular()
          apiFn = apiService.fetchPopular
          break
        case 'mostPopular':
          queryKey = queryKeys.platformInsight.mostPopular()
          apiFn = apiService.fetchMostPopular
          break
        case 'preOrders':
          queryKey = queryKeys.platformInsight.preOrders()
          apiFn = apiService.fetchPreOrders
          break
        default:
          return
      }

      const cached = queryClient.getQueryData(queryKey)
      if (cached) return

      return queryClient.prefetchQuery({
        queryKey,
        queryFn: apiFn,
        staleTime: 1000 * 60 * 5,
      })
    }

    this.addToPrefetchQueue(prefetchFn)
  }

  // Prefetch custom insight data
  prefetchCustomInsight(type) {
    const prefetchFn = async () => {
      let queryKey, apiFn

      switch (type) {
        case 'topGrossing':
          queryKey = queryKeys.customInsight.topGrossing()
          apiFn = apiService.fetchTopGrossing
          break
        case 'topImproved':
          queryKey = queryKeys.customInsight.topImproved()
          apiFn = apiService.fetchTopImproved
          break
        case 'hits':
          queryKey = queryKeys.customInsight.hits()
          apiFn = apiService.fetchHits
          break
        case 'topRated':
          queryKey = queryKeys.customInsight.topRated()
          apiFn = apiService.fetchTopRated
          break
        case 'topDownloaded':
          queryKey = queryKeys.customInsight.topDownloaded()
          apiFn = apiService.fetchTopDownloaded
          break
        case 'topReviewed':
          queryKey = queryKeys.customInsight.topReviewed()
          apiFn = apiService.fetchTopReviewed
          break
        default:
          return
      }

      const cached = queryClient.getQueryData(queryKey)
      if (cached) return

      return queryClient.prefetchQuery({
        queryKey,
        queryFn: apiFn,
        staleTime: 1000 * 60 * 5,
      })
    }

    this.addToPrefetchQueue(prefetchFn)
  }

  // Prefetch category analysis data
  prefetchCategoryAnalysis() {
    const prefetchFn = async () => {
      const keywordsCached = queryClient.getQueryData(queryKeys.categories.keywords())

      if (!keywordsCached) {
        return queryClient.prefetchQuery({
          queryKey: queryKeys.categories.keywords(),
          queryFn: apiService.fetchCategoryKeywords,
          staleTime: 1000 * 60 * 5,
        })
      }
    }

    this.addToPrefetchQueue(prefetchFn)
  }

  // Prefetch publisher analysis data
  prefetchPublisherAnalysis() {
    const prefetchFn = async () => {
      // This will be triggered when we have a specific publisher ID
      // For now, we can prefetch common publisher data patterns
      console.log('Publisher analysis prefetch ready')
    }

    this.addToPrefetchQueue(prefetchFn)
  }

  // Prefetch app tracking data
  prefetchAppTracking() {
    const prefetchFn = async () => {
      const cached = queryClient.getQueryData(queryKeys.apps.tracking({}))
      if (cached) return

      return queryClient.prefetchQuery({
        queryKey: queryKeys.apps.tracking({}),
        queryFn: () => apiService.fetchAppTrackingData({}),
        staleTime: 1000 * 60 * 5,
      })
    }

    this.addToPrefetchQueue(prefetchFn)
  }

  // Prefetch keyword tracking data
  prefetchKeywordTracking() {
    const prefetchFn = async () => {
      const cached = queryClient.getQueryData(queryKeys.keywords.predefined())
      if (cached) return

      return queryClient.prefetchQuery({
        queryKey: queryKeys.keywords.predefined(),
        queryFn: apiService.fetchPredefinedKeywords,
        staleTime: 1000 * 60 * 5,
      })
    }

    this.addToPrefetchQueue(prefetchFn)
  }

  // Prefetch category tracking data
  prefetchCategoryTracking() {
    const prefetchFn = async () => {
      const cached = queryClient.getQueryData(queryKeys.categories.keywords())
      if (cached) return

      return queryClient.prefetchQuery({
        queryKey: queryKeys.categories.keywords(),
        queryFn: apiService.fetchCategoryKeywords,
        staleTime: 1000 * 60 * 5,
      })
    }

    this.addToPrefetchQueue(prefetchFn)
  }

  // Prefetch user profile data
  prefetchUserProfile() {
    const prefetchFn = async () => {
      const cached = queryClient.getQueryData(queryKeys.user.profile())
      if (cached) return

      return queryClient.prefetchQuery({
        queryKey: queryKeys.user.profile(),
        queryFn: apiService.fetchUserProfile,
        staleTime: 1000 * 60 * 5,
      })
    }

    this.addToPrefetchQueue(prefetchFn)
  }

  // Prefetch user plans data
  prefetchUserPlans() {
    const prefetchFn = async () => {
      const cached = queryClient.getQueryData(queryKeys.user.plans())
      if (cached) return

      return queryClient.prefetchQuery({
        queryKey: queryKeys.user.plans(),
        queryFn: apiService.fetchUserPlans,
        staleTime: 1000 * 60 * 5,
      })
    }

    this.addToPrefetchQueue(prefetchFn)
  }

  // Prefetch user payment data
  prefetchUserPayment() {
    const prefetchFn = async () => {
      const cached = queryClient.getQueryData(queryKeys.user.paymentMethods())
      if (cached) return

      return queryClient.prefetchQuery({
        queryKey: queryKeys.user.paymentMethods(),
        queryFn: apiService.fetchUserPaymentMethods,
        staleTime: 1000 * 60 * 5,
      })
    }

    this.addToPrefetchQueue(prefetchFn)
  }

  // Prefetch chat bot data (placeholder)
  prefetchChatBot() {
    const prefetchFn = async () => {
      // Placeholder for chat bot prefetching
      console.log('Chat bot prefetch ready')
    }

    this.addToPrefetchQueue(prefetchFn)
  }
}

// Create singleton instance
export const prefetchManager = new PrefetchManager()

// Route-based prefetching
export const routePrefetchMap = {
  '/dashboard': () => prefetchManager.prefetchDashboard(),
  '/trending': () => prefetchManager.prefetchPlatformInsight('topApps'),
  '/new-releases': () => prefetchManager.prefetchPlatformInsight('preOrders'),
  '/top-apps': () => prefetchManager.prefetchPlatformInsight('topApps'),
  '/popular': () => prefetchManager.prefetchPlatformInsight('popular'),
  '/most-popular': () => prefetchManager.prefetchPlatformInsight('mostPopular'),
  '/pre-orders': () => prefetchManager.prefetchPlatformInsight('preOrders'),
  '/topgross': () => prefetchManager.prefetchCustomInsight('topGrossing'),
  '/topimproved': () => prefetchManager.prefetchCustomInsight('topImproved'),
  '/top-keywords-categories': () => prefetchManager.prefetchCustomInsight('TopKeywordsCategories'),
  '/top-rated': () => prefetchManager.prefetchCustomInsight('topRated'),
  '/top-downloaded': () => prefetchManager.prefetchCustomInsight('topDownloaded'),
  '/top-reviewed': () => prefetchManager.prefetchCustomInsight('topReviewed'),

  // Category Analysis
  '/categoryanalysis': () => prefetchManager.prefetchCategoryAnalysis(),

  // Publisher Analysis
  '/publisheranalysis': () => prefetchManager.prefetchPublisherAnalysis(),
  '/publisher-analysis': () => prefetchManager.prefetchPublisherAnalysis(),
  '/publisher-apps': () => prefetchManager.prefetchPublisherAnalysis(),

  // App Tracking
  '/app-tracking': () => prefetchManager.prefetchAppTracking(),

  // Keyword Tracking
  '/keywordSearchNew': () => prefetchManager.prefetchKeywordTracking(),
  '/keywordSearch': () => prefetchManager.prefetchKeywordTracking(),
  '/keyword-tracking': () => prefetchManager.prefetchKeywordTracking(),
  '/keyword-analysis': () => prefetchManager.prefetchKeywordTracking(),

  // Category Tracking
  '/category-tracking': () => prefetchManager.prefetchCategoryTracking(),

  // User Profile
  '/profile': () => prefetchManager.prefetchUserProfile(),
  '/profile/settings': () => prefetchManager.prefetchUserProfile(),
  '/profile/plans': () => prefetchManager.prefetchUserPlans(),
  '/profile/payment': () => prefetchManager.prefetchUserPayment(),

  // Chat Bot
  '/chatbot': () => prefetchManager.prefetchChatBot(),
}

// Hover-based prefetching hook
export const usePrefetchOnHover = (route, delay = 300) => {
  let timeoutId = null

  const handleMouseEnter = () => {
    timeoutId = setTimeout(() => {
      const prefetchFn = routePrefetchMap[route]
      if (prefetchFn) {
        prefetchFn()
      }
    }, delay)
  }

  const handleMouseLeave = () => {
    if (timeoutId) {
      clearTimeout(timeoutId)
      timeoutId = null
    }
  }

  return { handleMouseEnter, handleMouseLeave }
}

export default prefetchManager
